// Maps the canonical published-plan DTO (dashboard.plan.list) onto the
// task-board shapes. The v2 board is the published canonical weekly plan only:
// preexisting legacy homework rows are never merged in here — they remain on
// the Classic compatibility/history path until separate retirement.

import type { HomeworkTask, Sprint, TaskType } from './types'

// Dates cross the wire as ISO strings (no tRPC transformer), but accept Date
// too so the adapter stays correct if a transformer is ever added.
type WireDate = string | Date

export interface PlanWireAssignment {
  id: string
  title: string
  scheduledDate: WireDate
  compatibilityType: string
  instructions: string | null
  coachNote: string | null
  universalNotes: string | null
  description: string | null
  href: string | null
  track: string
  // Never null on the wire: the server resolves estimates through the total
  // engine (override ?? derived ?? catalog ?? default).
  estimatedMinutes: number
  status: string
  completionMode: string
  assignmentVersion: number
}

export interface PlanWireDay {
  id: string
  dayDate: WireDate
  kind: string
  assignments: PlanWireAssignment[]
}

export interface PlanWireWeek {
  id: string
  weekNumber: number
  startsOn: WireDate
  status: 'published' | 'closed'
  publishedAt: WireDate
  days: PlanWireDay[]
}

export interface PlanWire {
  engagementId: string
  weeks: PlanWireWeek[]
}

export interface PlanHomeworkData {
  tasks: HomeworkTask[]
  sprints: Sprint[]
}

const KIND_TO_TASK_TYPE: Record<string, TaskType> = {
  PROBLEM: 'leetcode',
  EXERCISE: 'exercise',
  SYSTEM_DESIGN: 'system-design',
  SOURCE_CONTENT: 'article',
  TASK: 'task',
  OTHER: 'task',
}

const DAY_MS = 86_400_000

/** Canonical plan dates are UTC-midnight instants; key them in UTC. */
function dateKeyOf(value: WireDate): string {
  const iso = value instanceof Date ? value.toISOString() : value
  return iso.slice(0, 10)
}

function dateKeyPlusDays(key: string, days: number): string {
  return new Date(new Date(`${key}T00:00:00.000Z`).getTime() + days * DAY_MS)
    .toISOString()
    .slice(0, 10)
}

export function planToHomework(plan: PlanWire | undefined): PlanHomeworkData {
  if (!plan) return { tasks: [], sprints: [] }

  const tasks: HomeworkTask[] = []
  const sprints: Sprint[] = []

  for (const week of plan.weeks) {
    const startDate = dateKeyOf(week.startsOn)
    sprints.push({
      number: week.weekNumber,
      startDate,
      endDate: dateKeyPlusDays(startDate, 6),
      theme: '',
    })

    for (const day of week.days) {
      for (const assignment of day.assignments) {
        const submissionReview =
          assignment.completionMode === 'submission_review'
        tasks.push({
          id: assignment.id,
          title: assignment.title,
          type: KIND_TO_TASK_TYPE[assignment.compatibilityType] ?? 'task',
          date: dateKeyOf(assignment.scheduledDate),
          description: assignment.instructions ?? assignment.description ?? '',
          coachNote: assignment.coachNote,
          universalNotes: assignment.universalNotes,
          links: assignment.href
            ? [{ label: 'resource', url: assignment.href }]
            : [],
          platformTracked: submissionReview,
          completed: assignment.status === 'completed',
          comments: [],
          commentsRead: true,
          assignmentVersion: assignment.assignmentVersion,
          completionMode: submissionReview ? 'submission_review' : 'manual',
        })
      }
    }
  }

  return { tasks, sprints }
}
